import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "@/lib/apiBase";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useConfirm } from "@/components/ConfirmDialogProvider";
import { toastError, toastSuccess } from "@/lib/toast";

const SECTIONS = [
  { value: "posts", label: "Posts" },
  { value: "cards", label: "Cards" },
  { value: "channels", label: "Channels" },
];

function formatWhen(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function itemLabel(item) {
  return item.title || item.preview || item.channel_title || `#${item.id}`;
}

/**
 * Admin Archive: restore soft-deleted posts, cards, and channels.
 * Soft-deleted comments stay deleted as tombstones (thread shape preserved).
 */
const OrgArchivePanel = ({ orgId, accessToken }) => {
  const confirm = useConfirm();
  const [section, setSection] = useState("posts");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restoringId, setRestoringId] = useState(null);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  const loadSection = useCallback(async () => {
    if (!orgId || !accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/org/${orgId}/archive`, {
        headers: authHeaders(),
        params: { section, limit: 50, offset: 0 },
      });
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      console.error(err);
      setItems([]);
      setError(
        err.response?.data?.message || "Failed to load archive"
      );
    } finally {
      setLoading(false);
    }
  }, [orgId, accessToken, section, authHeaders]);

  useEffect(() => {
    loadSection();
  }, [loadSection]);

  const handleRestore = async (item) => {
    const ok = await confirm({
      title: "Restore from archive?",
      description:
        item.type === "post" || item.type === "card"
          ? "This restores the item. Soft-deleted comments stay deleted as tombstones so the thread shape is preserved."
          : "This restores the selected item.",
      confirmLabel: "Restore",
    });
    if (!ok) return;

    const key = `${item.type}:${item.id}`;
    setRestoringId(key);
    try {
      await axios.post(
        `${API_BASE_URL}/org/${orgId}/archive/restore`,
        { type: item.type, id: item.id },
        { headers: authHeaders() }
      );
      toastSuccess("Restored");
      await loadSection();
    } catch (err) {
      toastError(err, "Restore failed");
    } finally {
      setRestoringId(null);
    }
  };

  const renderRows = (rows, emptyMessage) => {
    if (!rows.length) {
      return <p className="text-sm text-muted-foreground py-4">{emptyMessage}</p>;
    }
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="py-2 pr-3 font-medium">Preview</th>
              <th className="py-2 pr-3 font-medium">Context</th>
              <th className="py-2 pr-3 font-medium">Deleted</th>
              <th className="py-2 pr-3 font-medium">By</th>
              <th className="py-2 font-medium w-[1%] whitespace-nowrap"> </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => {
              const key = `${item.type}:${item.id}`;
              const contextParts = [];
              if (item.team_name) contextParts.push(item.team_name);
              if (item.type === "card" && item.kanban_status) {
                contextParts.push(item.kanban_status);
              }
              return (
                <tr key={key} className="border-b border-border/60 align-top">
                  <td className="py-3 pr-3">
                    <div className="font-medium text-foreground">
                      {item.permalink ? (
                        <Link
                          to={item.permalink}
                          className="hover:underline"
                        >
                          {itemLabel(item)}
                        </Link>
                      ) : (
                        itemLabel(item)
                      )}
                    </div>
                    {item.preview &&
                      item.preview !== item.title && (
                        <p className="text-muted-foreground mt-0.5 line-clamp-2">
                          {item.preview}
                        </p>
                      )}
                  </td>
                  <td className="py-3 pr-3 text-muted-foreground">
                    {contextParts.length ? contextParts.join(" · ") : "—"}
                  </td>
                  <td className="py-3 pr-3 text-muted-foreground whitespace-nowrap">
                    {formatWhen(item.deleted_at)}
                  </td>
                  <td className="py-3 pr-3 text-muted-foreground">
                    {item.deleted_by?.name || "—"}
                  </td>
                  <td className="py-3">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={restoringId === key}
                      onClick={() => handleRestore(item)}
                    >
                      {restoringId === key ? "Restoring…" : "Restore"}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h3 className="text-base font-semibold">Archive</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Restore soft-deleted posts, cards, and channels. Comments are not
          restored — they remain tombstones so thread structure stays intact.
          Direct messages are not listed. Older deletes that wiped content
          cannot be restored.
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Tabs value={section} onValueChange={setSection}>
        <TabsList>
          {SECTIONS.map((s) => (
            <TabsTrigger key={s.value} value={s.value}>
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {SECTIONS.map((s) => (
          <TabsContent key={s.value} value={s.value} className="mt-4">
            {loading && section === s.value ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              renderRows(
                section === s.value ? items : [],
                `No deleted ${s.label.toLowerCase()} with restorable content.`
              )
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default OrgArchivePanel;
